/**
 * Stereo split-view mirroring for a 2D overlay (the stats panel, the
 * playback bar) laid over the canvas.
 *
 * An SBS 3D display shows each canvas half to one eye only, so an overlay
 * sitting in one half is seen by one eye. When enabled, the overlay moves
 * into one half (`originalSide`) and a display-only copy goes into the
 * other at the same relative spot, so both eyes see it and the two fuse
 * into one. The original goes on the side the overlay is anchored to, so
 * logic keyed on real screen coordinates (the dataset rail's left-edge
 * reveal zone) still lines up with it.
 *
 * `anamorphic` matches camera.splitAnamorphic: the display stretches each
 * half 2x horizontally, so each copy is laid out against the full canvas
 * width and squeezed by 0.5 - after the stretch it reads at its normal size
 * and position. Without it (the ?ftgsSbsDual 2D demo) each half is laid
 * out at its own width, unsqueezed.
 *
 * Only the original takes input; the copy ignores the pointer, so the
 * camera under it still gets drags. The original's hover state is copied
 * over as the `ftgs-stereo-hover` class (see applyHover), which each
 * overlay's tooltip rules honour alongside :hover, so the black hover
 * bubbles show in both eyes. Disabling restores the overlay to its normal
 * place. Layout styles live in ftgs-stereo-mirror.scss.
 */

const HOVER_CLASS = 'ftgs-stereo-hover';

/** child-node index path from `root` down to `node` (null if outside root) */
const pathFrom = (root: Node, node: Node): number[] | null => {
    const path: number[] = [];
    for (let n = node; n !== root; n = n.parentNode) {
        if (!n.parentNode) {
            return null;
        }
        path.unshift(Array.prototype.indexOf.call(n.parentNode.childNodes, n));
    }
    return path;
};

const nodeAt = (root: Node, path: number[]): Node | null => {
    let n: Node | null = root;
    for (const i of path) {
        n = n?.childNodes[i] ?? null;
    }
    return n;
};

/** copy `src`'s attributes onto `dst`, dropping any `src` no longer has */
const syncAttributes = (src: Element, dst: Element) => {
    for (const name of dst.getAttributeNames()) {
        if (!src.hasAttribute(name)) {
            dst.removeAttribute(name);
        }
    }
    for (const name of src.getAttributeNames()) {
        const value = src.getAttribute(name);
        if (dst.getAttribute(name) !== value) {
            dst.setAttribute(name, value);
        }
    }
};

/**
 * Bring `dst`'s children in line with `src`'s IN PLACE - text and
 * attributes are patched, only nodes whose type/tag changed are re-cloned.
 * Keeping the copy's elements alive (rather than re-cloning the subtree on
 * every change) is what lets their CSS transitions run in step with the
 * original's: the tooltip bubbles' 0.25s show delay, the fades. The stats
 * panel changes every frame (camera position), so a re-clone would restart
 * those every frame and they would never finish.
 */
const patchChildren = (src: Node, dst: Node) => {
    const srcChildren = src.childNodes;
    while (dst.childNodes.length > srcChildren.length) {
        dst.lastChild.remove();
    }
    for (let i = 0; i < srcChildren.length; i++) {
        const s = srcChildren[i];
        const d = dst.childNodes[i];
        if (!d) {
            dst.appendChild(s.cloneNode(true));
        } else if (s.nodeType !== d.nodeType || s.nodeName !== d.nodeName) {
            d.replaceWith(s.cloneNode(true));
        } else if (s.nodeType === Node.TEXT_NODE) {
            if (d.nodeValue !== s.nodeValue) {
                d.nodeValue = s.nodeValue;
            }
        } else if (s instanceof Element && d instanceof Element) {
            syncAttributes(s, d);
            // a range input's current position is a property, not an
            // attribute, so neither cloneNode nor the attribute sync carry it
            if (s instanceof HTMLInputElement && d instanceof HTMLInputElement && d.value !== s.value) {
                d.value = s.value;
            }
            patchChildren(s, d);
        }
    }
};

type StereoSide = 'left' | 'right';

class FtgsStereoMirror {
    private readonly source: HTMLElement;
    private readonly originalSide: StereoSide;
    private layer: HTMLDivElement | null = null;
    private mirrorHost: HTMLDivElement | null = null;
    private mirror: HTMLElement | null = null;
    private observer: MutationObserver | null = null;

    // deepest element under the pointer inside the original (null = none)
    private hovered: Element | null = null;
    private hoverMarked: Element[] = [];

    private readonly onPointerOver = (event: PointerEvent) => {
        this.hovered = event.target instanceof Element ? event.target : null;
        this.applyHover();
    };

    private readonly onPointerLeave = () => {
        this.hovered = null;
        this.applyHover();
    };

    // 창이 다른 화면(글래스)으로 넘어가면 pointerleave 가 오지 않아 복사본에 호버 표시가 남을 수
    // 있음(2026-10-31) - 전체 화면이 바뀔 때 원본이 실제로 호버 중인지 다시 확인
    private readonly onFullscreenChange = () => {
        if (this.hovered && !this.hovered.matches(':hover, .ftgs-stereo-hover')) {
            this.hovered = null;
            this.applyHover();
        }
    };

    // a scrolled overlay (the stats panel past the window height) scrolls
    // the copy along - scroll position is neither an attribute nor a DOM
    // change, so the observer never sees it
    private readonly onScroll = () => {
        if (this.mirror) {
            this.mirror.scrollTop = this.source.scrollTop;
        }
    };

    constructor(source: HTMLElement, originalSide: StereoSide = 'right') {
        this.source = source;
        this.originalSide = originalSide;
    }

    setEnabled(enabled: boolean, anamorphic: boolean) {
        if (!enabled) {
            this.teardown();
            return;
        }

        if (!this.layer) {
            const parent = this.source.parentElement;
            if (!parent) {
                return;
            }

            const layer = document.createElement('div');
            layer.className = 'ftgs-stereo-layer';
            // the overlay's own z-index now only orders it inside its half,
            // so the layer takes it over to keep the overlay's place among
            // its siblings (the dataset rail's 10, the version tag's 9999)
            const zIndex = getComputedStyle(this.source).zIndex;
            if (zIndex !== 'auto') {
                layer.style.zIndex = zIndex;
            }
            const makeHalf = (side: StereoSide) => {
                const half = document.createElement('div');
                half.className = `ftgs-stereo-half ftgs-stereo-half-${side}`;
                const inner = document.createElement('div');
                inner.className = 'ftgs-stereo-inner';
                half.appendChild(inner);
                layer.appendChild(half);
                return inner;
            };
            // the original's half goes first in document order, so the
            // copy's duplicate ids (kept - the overlays' styles are keyed on
            // them) never shadow the original in an id lookup
            const originalHost = makeHalf(this.originalSide);
            this.mirrorHost = makeHalf(this.originalSide === 'right' ? 'left' : 'right');

            parent.insertBefore(layer, this.source);
            originalHost.appendChild(this.source);
            this.layer = layer;

            // Re-copy whenever the original changes - text, classes (incl.
            // show/hide), inline styles (the scrubber's fill/thumb), and the
            // slider readouts, which every slider value change (dragged or
            // set from code) rewrites. The callback is already batched per
            // microtask, and the copy lives outside the source, so copying
            // never re-triggers it.
            this.observer = new MutationObserver(() => this.sync());
            this.observer.observe(this.source, { subtree: true, childList: true, characterData: true, attributes: true });
            this.source.addEventListener('pointerover', this.onPointerOver);
            this.source.addEventListener('pointerleave', this.onPointerLeave);
            this.source.addEventListener('scroll', this.onScroll);
            document.addEventListener('fullscreenchange', this.onFullscreenChange);
            this.sync();
        }

        this.layer.classList.toggle('ftgs-stereo-anamorphic', anamorphic);
    }

    private sync() {
        if (!this.mirrorHost) {
            return;
        }
        // The copy is patched in place, root included (see patchChildren),
        // so the overlays' fades on the root - the playback bar's idle
        // fade, the rail's reveal, the version tag's inline opacity - also
        // run on the copy in step with the original.
        if (!this.mirror) {
            this.mirror = this.source.cloneNode(false) as HTMLElement;
            this.mirrorHost.appendChild(this.mirror);
        }
        const copy = this.mirror;
        syncAttributes(this.source, copy);
        copy.classList.add('ftgs-stereo-mirror');
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        patchChildren(this.source, copy);

        // the attribute sync just wiped the hover marks - put them back in
        // the same task, before any style recalc, so no transition sees
        // them drop
        this.hoverMarked = [];
        this.applyHover();
        // content changes can change how far the original is scrolled
        copy.scrollTop = this.source.scrollTop;
    }

    /**
     * Mirror :hover onto the copy. :hover holds for the element under the
     * pointer AND all its ancestors, so the same chain is marked - a
     * tooltip on a label shows while the pointer is over the label's text.
     */
    private applyHover() {
        if (!this.mirror) {
            return;
        }
        const marked: Element[] = [];
        const path = this.hovered ? pathFrom(this.source, this.hovered) : null;
        if (path) {
            for (let len = path.length; len >= 0; len--) {
                const node = nodeAt(this.mirror, path.slice(0, len));
                if (node instanceof Element) {
                    marked.push(node);
                }
            }
        }
        for (const el of this.hoverMarked) {
            if (!marked.includes(el)) {
                el.classList.remove(HOVER_CLASS);
            }
        }
        for (const el of marked) {
            el.classList.add(HOVER_CLASS);
        }
        this.hoverMarked = marked;
    }

    private teardown() {
        if (!this.layer) {
            return;
        }
        this.observer?.disconnect();
        this.source.removeEventListener('pointerover', this.onPointerOver);
        this.source.removeEventListener('pointerleave', this.onPointerLeave);
        this.source.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        this.hovered = null;
        this.hoverMarked = [];
        this.layer.parentElement?.insertBefore(this.source, this.layer);
        this.layer.remove();
        this.layer = null;
        this.mirrorHost = null;
        this.mirror = null;
        this.observer = null;
    }
}

export { FtgsStereoMirror, StereoSide };
